import { helpers } from './helpers';
import { modals } from './modals';
import { formValidation } from './form-validation';
import { rules as formValidationRules } from './form-validation.rules';
import { app } from './app';
import "regenerator-runtime/runtime";


// ****************************************** //      
// *                                        * //
// *           SCHEMA DEFINITIONS           * //
// *                                        * //
// ****************************************** //

/**
 * @typedef {Object} Model
 * @property {boolean} leadSynced - Whether the lead has been changed since it was synced with the server.
 * @property {Lead} [lead] - The lead data object containing form values
 * @property {number} [step] - The current step in the form flow (1-4)
 * @property {string} [langIso] - The language ISO code
 * @property {boolean} gotModalMarkup - Whether the modal markup is loaded
 */

/**
 * @typedef {Object} Lead
 * @property {string} [id] - The unique identifier for this lead
 * @property {string} [company_name] - Company name
 * @property {string} [contact_name] - Contact person's name
 * @property {string} [contact_email] - Contact email address
 * @property {string} [contact_phone] - Contact phone number
 * @property {string} [room_option] - Selected room/space type
 * @property {string} [style_preference] - Selected style/color preferences
 * @property {number} [number_of_seats] - Number of workspaces needed
 * @property {string} [org_number] - Organization number if provided
 * @property {string} [budget] - Budget amount if provided
 * @property {string} [desired_delivery_date] - Desired delivery date
 * @property {string} [additional_notes] - Any additional information
 * @property {number} [revision] - The revision number of the lead
 * @property {boolean} [sealed] - Whether the lead is sealed
 * @property {Array<{name: string, size: number}>} [attachments] - Array of attachments
 * 
 * @todo the server is a bit to generous with information. We should only get the fields that the frontend has use for.
 * @example from server: 
 *  {
 *  "code": 200,
 *  "message": "Lead loaded",
 *  "lead": {
 *      "id": "xYjOnA",
 *      "id_crm": "580bd84a-a3e3-ef11-8eea-000d3aaaa151",
 *      "id_shop": "1",
 *      "created_at": "2025-02-05 09:26:40",
 *      "updated_at": "2025-02-05 09:38:27",
 *      "location": "Stockholm (Stockholm), SE [0]",
 *      "company_name": "dpj-test",
 *      "contact_name": "johan strand",
 *      "contact_email": "johan.strand@dpj.se",
 *      "contact_phone": "0736232847",
 *      "room_option": "entire_office",
 *      "style_preference": "scandinavian",
 *      "number_of_seats": "45",
 *      "org_number": "5565129193",
 *      "budget": "5000000",
 *      "desired_delivery_date": "2025-05-06",
 *      "additional_notes": "nytt försök",
 *      "sealed": "0",
 *      "revision": "9",
 *      "crm_synced_revision": "9",
 *      "crm_sync_lock": null
 *  },
 *  "attachments": [
 *      {
 *      "name": "Screenshot 2025-01-29 161147.png",
 *      "size": "262073",
 *      "mime_type": "image/png",
 *      "uploaded_to_crm": "Yes",
 *      "content_hash": "8f152811d0abec9f5bc49cf4e0aba69b"
 *      },
 *      {
 *      "name": "Screenshot 2025-01-29 160807.png",
 *      "size": "24329",
 *      "mime_type": "image/png",
 *      "uploaded_to_crm": "Yes",
 *      "content_hash": "f2f76756ff1b8b6cb9c488ae9da7c65a"
 *      }
 *  ]}
 */

/** 
 * These messages defines the events that should update the state of the form.
 * @typedef {{
 *   type: "gotLeadFromServer",
 *   data: {lead: Lead}
 * } | {
 *   type: "gotLeadFromServerError", 
 *   data: {error: "timeout"|"not_found"|"other", message: string}
 * } | {
 *   type: "userChangedField",
 *   data: {field: string, value: string}
 * } | {
 *   type: "clickNext",
 *   data: null
 * } | {
 *   type: "clickPrev",
 *   data: null
 * } | {
 *   type: "clickSendLead",
 *   data: null
 * } | {
 *   type: "modalClosed",
 *   data: null
 * } | {
 *   type: "gotModalMarkup",
 *   data: {modal: HTMLDivElement}
 * } | {
 *   type: "gotModalMarkupError",
 *   data: {error: "timeout"|"other", message: string}
 * } | { 
 *   type: "createLeadSuccess",
 *   data: {lead: Lead}
 * } | {
 *   type: "createLeadError",
 *   data: {error: "timeout"|"other", message: string}
 * } | { 
 *   type: "patchSuccess",
 *   data: {lead: Lead}
 * } | {
 *   type: "patchError",
 *   data: {error: "timeout"|"sealed"|"other", message: string}
 * } | {
 *   type: "gotLocation",
 *   data: {location: string}
 * } | {
 *   type: "finalizeLeadSuccess",
 *   data: null
 * } | {
 *   type: "finalizeLeadError",
 *   data: {error: "timeout"|"bad_revision"|"other", message: string}
 * } | {
 *   type: "fileAdded",
 *   data: {file: File}
 * } | {
 *   type: "fileUploaded",
 *   data: {lead: Lead}
 * } | {
 *   type: "fileUploadError",
 *   data: {error: "timeout"|"bad_request"|"other", message: string}
 * } | {
 *   type: "noop",
 *   data: null
 * }} Msg
 */

/** @typedef {Promise<Msg>} Task */

export const inredningshjalpen = {
    /** @type {Model} */
    model: { step: 1, lead: null, langIso: "en", gotModalMarkup: false, leadSynced: true }, // Only updated by update. consider it read only.
    /** @type {Task[]} */
    tasks: [],
    tasksRunning: false,

    // ****************************************** //      
    // *                                        * //
    // *                 RUNTIME                * //
    // *                                        * //
    // ****************************************** //

    /** 
     * @param {Task} task 
     * @returns {void}
     */
    taskEnqueue: (task) => {
        // console.debug("inredningshjalpen.js: R1207: taskEnque: task", task);
        inredningshjalpen.tasks.push(task);
        inredningshjalpen.taskRun();
    },

    /** 
     * @returns {Task} 
     */
    taskPop: () => {
        return inredningshjalpen.tasks.shift();
    },

    /** 
     * @returns {void}
     */
    taskRun: async () => {
        if (inredningshjalpen.tasksRunning) {
            return;
        }
        inredningshjalpen.tasksRunning = true;
        while (inredningshjalpen.tasks.length > 0) {
            const task = inredningshjalpen.taskPop();
            let msg;
            try {
                msg = await task;
            } catch (error) {
                // tasks should not error but instead handle the error and return a message for the update function.
                console.error("inredningshjalpen.js: taskRunner: task: ", task);
                console.error("inredningshjalpen.js: taskRunner: error: ", error);
            }

            inredningshjalpen.update(msg);
        }
        inredningshjalpen.tasksRunning = false;
    },
    // ****************************************** //      
    // *                                        * //
    // *              UPDATE STATE              * //
    // *                                        * //
    // ****************************************** //

    init: () => {
        console.debug("inredningshjalpen.js: init");
        try {
            inredningshjalpen.model.langIso = prestashop.language.iso_code;
            const scrollButtons = document.querySelectorAll(".scroll-to-contact");
            scrollButtons.forEach(button => {
                button.addEventListener("click", () => {
                    inredningshjalpen.scrollToInitialForm();
                });
            });

            const form = document.getElementById("inredningshjalpen-form");
            inredningshjalpen.injectDataTags(inredningshjalpen.model.langIso, form);
            formValidation.addListeners(form,
                (event) => {
                    event.preventDefault();
                    inredningshjalpen.update({ type: "clickNext", data: null });
                },
            );

            form.addEventListener("change", (event) => {
                inredningshjalpen.update({ type: "userChangedField", data: { field: event.target.name, value: event.target.value } });
            });

            // Inredningshjälpen video
            if (helpers.isSmallDevice()) {
                $(".block_video_player").html('<iframe style="width:100%;height:300px;" src="https://www.youtube.com/embed/MgEnbgZkh-o" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>');
            } else {
                $(".block_video_player").html('<iframe style="width:100%;height:700px;" src="https://www.youtube.com/embed/MgEnbgZkh-o" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>');
            }

        } catch (error) {
            console.error("inredningshjalpen.js: init: error: ", error);
        }
        try {
            const { id } = inredningshjalpen.getUrlParams();
            if (id) {
                // 15 seconds timeout on initial page load
                inredningshjalpen.taskEnqueue(inredningshjalpen.task_loadLead(id, 15_000));
            }
            // 30 seconds timeout on initial page load
            inredningshjalpen.taskEnqueue(inredningshjalpen.task_modalGet(30_000));
        } catch (error) {
            console.error("inredningshjalpen.js: init: error: ", error);
        }


    },

    /** 
     * Update the model state based on the message.
     * @param {Msg} msg - The message to update the model state
     * @returns {void}
     */
    update: (msg) => {
        try {
            const m = JSON.parse(JSON.stringify(inredningshjalpen.model));
            // console.log("inredningshjalpen.js: PRE update: model", m, "\nmsg", msg);
            inredningshjalpen.validateMsg(msg);
            switch (msg.type) {
                case "gotLeadFromServer":
                    m.lead = msg.data.lead;
                    m.leadSynced = true;
                    if (m.step === 1) {
                        inredningshjalpen.scrollToInitialForm();
                    }
                    break;
                case "gotLeadFromServerError":
                    m.step = 1;
                    m.lead = null;
                    m.leadSynced = true;
                    inredningshjalpen.resetForm();
                    if (msg.data.error.type === "not_found") {
                        inredningshjalpen.resetForm();
                        inredningshjalpen.setUrlParams(null)
                    } else {
                        inredningshjalpen.showError("Failed to load lead. Please try again or contact customer support if the problem persists.");
                    }
                    break;
                case "userChangedField":
                    m.leadSynced = false;
                    m.lead ??= {};
                    m.lead[msg.data.field] = msg.data.value;
                    break;
                case "clickNext":
                    if (m.leadSynced) {
                        m.step++;
                        break;
                    }
                    if (m.lead && m.lead.id && !m.leadSynced) {
                        m.lead = inredningshjalpen.normalizeLead(m.lead);
                        inredningshjalpen.taskEnqueue(inredningshjalpen.task_patch(m.lead));
                    }
                    if (!m.lead || !m.lead.id) {
                        inredningshjalpen.taskEnqueue(
                            inredningshjalpen.task_getLocation(
                                () => inredningshjalpen.task_create(m.lead)));

                        window.dataLayer.push({
                            'event': 'Inredningshjalp',
                            'event_category': 'Forms',
                            'event_label': 'Submit',
                        });
                    }
                    break;
                case "clickPrev":
                    m.step--;
                    break;
                case "clickSendLead":
                    console.assert(m.step === 4, "inredningshjalpen.js: update: clickSendLead: m.step is not 4");
                    m.lead = inredningshjalpen.normalizeLead(m.lead);
                    inredningshjalpen.taskEnqueue(inredningshjalpen.task_finalizeLead(m.lead));
                    break;
                case "modalClosed":
                    m.step = 1;
                    break;
                case "gotModalMarkup":
                    document.querySelector(".page-overlay").append(msg.data.modal);
                    app.dpjHacksAndFixes();
                    inredningshjalpen.modalAddPlaceholders(m.langIso);
                    inredningshjalpen.injectDataTagsModal(m.langIso, msg.data.modal.querySelector("form"));
                    inredningshjalpen.modalListeners();
                    if (m.lead) {
                        inredningshjalpen.modalPopulate(m.lead);
                    }
                    inredningshjalpen.addTextAreaCounter(msg.data.modal.querySelector("form"));
                    m.gotModalMarkup = true;
                    break;
                case "gotModalMarkupError":
                    m.gotModalMarkup = false;
                    inredningshjalpen.showError("Failed to load modal markup. Please try again or contact customer support if the problem persists.");
                    break;
                case "patchSuccess":
                    inredningshjalpen.setLoading(false);
                    m.leadSynced = true;
                    m.lead = msg.data.lead;
                    m.step++;
                    break;
                case "patchError":
                    inredningshjalpen.taskEnqueue(inredningshjalpen.task_loadLead(m.lead.id));
                    inredningshjalpen.setLoading(false);
                    inredningshjalpen.showError("Failed to update lead. Please try again or contact customer support if the problem persists.");
                    break;
                case "createLeadSuccess":
                    inredningshjalpen.setLoading(false);
                    m.leadSynced = true;
                    m.lead = msg.data.lead;
                    m.step++;
                    break;
                case "createLeadError":
                    inredningshjalpen.setLoading(false);
                    inredningshjalpen.showError("Failed to create lead. Please try again or contact customer support if the problem persists.");
                    break;
                case "gotLocation":
                    const fields = document.querySelectorAll("#inredningshjalpen-form .contact__location-field");
                    fields.forEach(field => {
                        field.value = msg.data.location;
                    });
                    break;
                case "finalizeLeadSuccess":
                    m.leadSynced = true;
                    m.lead = null;
                    m.step = 5;
                    inredningshjalpen.resetForm();
                    break;
                case "finalizeLeadError":
                    m.leadSynced = false;
                    inredningshjalpen.showError("Failed to finalize lead. Please try again or contact customer support if the problem persists.");
                    break;
                case "fileAdded":
                    /** @todo improve ui feedback.. "uploading...", "uploaded" etc.. */
                    console.assert(m.lead, "inredningshjalpen.js: update: fileAdded: m.lead is required");
                    console.assert(m.lead.id, "inredningshjalpen.js: update: fileAdded: m.lead.id is required");
                    inredningshjalpen.taskEnqueue(inredningshjalpen.task_upload(m.lead, msg.data.file));
                    break;
                case "fileUploaded":
                    /** @todo improve ui feedback.. "uploading...", "uploaded" etc.. */
                    m.leadSynced = true;
                    break;
                case "fileUploadError":
                    inredningshjalpen.showError("Failed to upload file. Please try again or contact customer support if the problem persists. (reason: " + msg.data.message + ")");
                    break;
                default:
                    console.error("inredningshjalpen.js: update: unknown message type: ", msg.type);
                    throw new Error("inredningshjalpen.js: update: unknown message type: " + msg.type);
            }

            // console.log("inredningshjalpen.js: POST update: model", m);
            inredningshjalpen.view(inredningshjalpen.model, m);
            inredningshjalpen.model = m;
        } catch (error) {
            console.error("inredningshjalpen.js: update: error: ", error);
        }
    },

    /** 
     * @param {Model} oldModel 
     * @param {Model} newModel 
     */
    view: (oldModel, newModel) => {
        // console.debug("inredningshjalpen.js: view: ", oldModel, "\n", newModel);

        const modalEl = document.getElementById("inredningshjalpen-modal");
        if (modalEl) {
            modalEl.dataset.step = newModel.step;
            if (oldModel.step === 1 && newModel.step > 1) {
                modalEl.style.display = "block";
                modals.togglePageOverlay(true);
            } else if (oldModel.step > 1 && newModel.step === 1) {
                modalEl.style.display = "none";
                modals.togglePageOverlay(false);
            }
        }

        if (newModel.step === 2) {
            const rightImage = document.querySelector("#inredningshjalpen-modal .inredningshjalpen-modal-right-image");
            rightImage.style.backgroundImage = "url('" + prestashop.urls.base_url + "themes/dpj/assets/img/digital-inredningshjalp/form-right-image.png')";
            if (typeof newModel.lead?.room_option === "undefined" || newModel.lead?.room_option === null || newModel.lead?.room_option === "") {
                document.querySelectorAll(".input-button.inredningshjalpen-modal__navigate--next").forEach(button => {
                    button.disabled = true;
                });
            } else {
                document.querySelectorAll(".input-button.inredningshjalpen-modal__navigate--next").forEach(button => {
                    button.disabled = false;
                });
            }
        }

        if (newModel.step === 3) {
            const rightImage = document.querySelector("#inredningshjalpen-modal .inredningshjalpen-modal-right-image");
            let stylePreference = newModel.lead?.style_preference;
            if (stylePreference) {
                rightImage.style.backgroundImage = "url('" + prestashop.urls.base_url + "themes/dpj/assets/img/digital-inredningshjalp/color-styles/" + stylePreference + ".png')";
            } else {
                rightImage.style.backgroundImage = "url('" + prestashop.urls.base_url + "themes/dpj/assets/img/digital-inredningshjalp/color-styles/surprise_me.png')";
            }

            if (typeof newModel.lead?.style_preference === "undefined" || newModel.lead?.style_preference === null || newModel.lead?.style_preference === "") {
                document.querySelectorAll(".input-button.inredningshjalpen-modal__navigate--next").forEach(button => {
                    button.disabled = true;
                });
            } else {
                document.querySelectorAll(".input-button.inredningshjalpen-modal__navigate--next").forEach(button => {
                    button.disabled = false;
                });
            }
        }

        if (newModel.gotModalMarkup && newModel.lead?.room_option) {
            inredningshjalpen.modalSetRoomOption(newModel.lead.room_option);
        }
        if (newModel.gotModalMarkup && newModel.lead?.style_preference) {
            inredningshjalpen.modalSetStylePreference(newModel.lead.style_preference);
        }

        // const leadChanged = JSON.stringify(newModel.lead) !== JSON.stringify(oldModel.lead);
        const leadChanged = true;

        if (leadChanged && newModel.lead?.id !== oldModel.lead?.id) {
            inredningshjalpen.setUrlParams(newModel.lead?.id ?? null);
        }
        if (leadChanged && newModel.lead && newModel.step === 1) {
            inredningshjalpen.formPopulate(newModel.lead);
        }
        if (leadChanged && newModel.lead && newModel.step > 1 && newModel.gotModalMarkup === true) {
            inredningshjalpen.modalPopulate(newModel.lead);
        }

    },

    // ****************************************** //      
    // *                                        * //
    // *                  TASKS                 * //
    // *                                        * //
    // * - these run async and send the given   * //
    // *   message to the update function upon  * //
    // *   completion                           * //
    // ****************************************** //

    /** 
     * @param {?number} timeout - The timeout in milliseconds
     * @returns {Task} 
     */
    task_modalGet: (timeout = 5_000) => {
        return new Promise((resolve) => {
            const timeoutPromise = helpers.timeoutPromise(timeout);
            const modalPromise = fetch(prestashop.urls.base_url + "content/136-inredningshjalpen-modal")

            Promise.race([timeoutPromise, modalPromise])
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`Server responded with status: ${response.status}`);
                    }
                    return response.text();
                })
                .then(data => {
                    const parser = new DOMParser();
                    const modalDocument = parser.parseFromString(data, "text/html").querySelector("#inredningshjalpen-modal");
                    if (!modalDocument) {
                        throw new Error("Modal content not found in response");
                    }
                    resolve({ type: "gotModalMarkup", data: { modal: modalDocument } });
                })
                .catch(error => {
                    // console.debug("inredningshjalpen.js: task_modalGet: error", error, "\n( type ", typeof error, ")");
                    if (error === "timeout") {
                        resolve({ type: "gotModalMarkupError", data: { error: { type: "timeout", message: error } } });
                    } else {
                        resolve({ type: "gotModalMarkupError", data: { error: { type: "other", message: error } } });
                    }
                });
        });
    },

    /**
     * @param {string} id - The lead id
     * @param {?number} timeout - The timeout in milliseconds
     * @returns {Task}
     */
    task_loadLead: (id, timeout = 5_000) => {
        return new Promise((resolve) => {
            inredningshjalpen.setLoading(true);
            const timeoutPromise = helpers.timeoutPromise(timeout);
            const leadPromise = fetch(prestashop.urls.base_url + `lead?token=${id}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                }
            })
            return Promise.race([timeoutPromise, leadPromise])
                .then(response => {
                    // console.debug("inredningshjalpen.js: task_loadLead: response", response);
                    switch (response.status) {
                        case 200:
                            return response.json();
                        case 404:
                            resolve({ type: "gotLeadFromServerError", data: { error: { type: "not_found", message: "Lead not found" } } });
                            break;
                        default:
                            return response.json().then(data => {
                                throw new Error(`E75: error submitting form, status: ${response.status}, data: ${JSON.stringify(data, null, 2)}`);
                            });
                    }
                })
                .then(data => {
                    // console.debug("inredningshjalpen.js: task_loadLead: data", data);
                    resolve({ type: "gotLeadFromServer", data: { lead: data.lead } });
                })
                .catch(error => {
                    // console.debug("inredningshjalpen.js: task_loadLead: error", error, "\n( type ", typeof error, ")");
                    if (error === "timeout") {
                        resolve({ type: "gotLeadFromServerError", data: { error: { type: "timeout", message: error } } });
                    } else {
                        resolve({ type: "gotLeadFromServerError", data: { error: { type: "other", message: error } } });
                    }
                })
                .finally(() => {
                    inredningshjalpen.setLoading(false);
                })
        });
    },

    /** 
     * @param {Lead} lead - The lead object
     * @param {?number} timeout - The timeout in milliseconds
     * @returns {Task} 
    */
    task_create: (lead, timeout = 5_000) => {
        console.assert(lead, "inredningshjalpen.js: task_create: lead is required");
        console.assert(typeof lead.id === "undefined", "inredningshjalpen.js: task_create: lead.id should not be set");

        const form = document.querySelector("#inredningshjalpen-form");
        const formData = new FormData(form);
        formData.set("id_shop_real", formData.get("id_shop"));
        formData.set("id_shop", 1);


        return new Promise((resolve) => {
            Promise.race([
                helpers.timeoutPromise(timeout),
                fetch("/lead", {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json'
                    },
                    body: formData
                })
            ]).then(response => {
                switch (response.status) {
                    case 201:
                        return response.json();
                    default:
                        return response.json().then(data => {
                            throw new Error(`inredningshjalpen.js: task_create: submitting initial form, status: ${response.status}, data: ${JSON.stringify(data)}`);
                        })
                }
            }).then(data => {
                resolve({ type: "createLeadSuccess", data: { lead: data.lead } });
            }).catch((err) => {
                if (err === "timeout") {
                    resolve({ type: "createLeadError", data: { error: { type: "timeout", message: err } } });
                } else {
                    resolve({ type: "createLeadError", data: { error: { type: "other", message: err } } });
                }
            })
        });
    },

    /**
     * @param {Lead} lead 
     * @param {?number} timeout - The timeout in milliseconds
     * @returns {Task}
     */
    task_patch: (lead, timeout = 5_000) => {
        return new Promise((resolve) => {
            const timeoutPromise = helpers.timeoutPromise(timeout);
            const formData = new FormData();

            // Add any additional lead data
            Object.keys(lead).forEach(key => {
                if (typeof lead[key] === "undefined" || lead[key] === null || lead[key] === "") return;
                if (key === "id") {
                    formData.set("token", lead[key]);
                } else {
                    formData.set(key, lead[key]);
                }
            });

            formData.set("patch", "1");
            formData.set("id_shop_real", formData.get("id_shop"));
            formData.set("id_shop", 1);
            // Make the fake PATCH request
            const patchPromise = fetch("/lead", {
                method: 'POST',
                headers: {
                    'Accept': 'application/json'
                },
                body: formData,
            });

            Promise.race([timeoutPromise, patchPromise])
                .then(response => {
                    if (response.status === 403) {
                        return resolve({ type: "patchError", data: { error: { type: "sealed", message: "This lead has already been submitted. Please contact customer support if you need to correct anything." } } });
                    }
                    if (!response.ok) {
                        return response.json().then(data => {
                            throw new Error(`Server error: ${response.status}, ${JSON.stringify(data)}`);
                        });
                    }
                    return response.json();
                })
                .then(data => {
                    resolve({ type: "patchSuccess", data: { lead: data.lead } });
                })
                .catch(error => {
                    console.error("inredningshjalpen.js: patch: error", error);
                    resolve({
                        type: "patchError",
                        data: {
                            error: {
                                type: error === "timeout" ? "timeout" : "other",
                                message: error.message || String(error)
                            }
                        }
                    });
                });
        });
    },

    /**
     * @param {?()=>Task} then - The function to call when the location is found
     * @param {?number} timeout - The timeout in milliseconds
     * @returns {Task}
     */
    task_getLocation: (then = null, timeout = 2_500) => {
        return new Promise((resolve) => {
            Promise.race([
                helpers.getLocationData(),
                helpers.timeoutPromise(timeout)
            ]).then((data) => {
                const locationString = `${data.city} (${data.region}), ${data.country} [${data.provider}]`;
                resolve({ type: "gotLocation", data: { location: locationString } });
            }).catch((err) => {
                // if no provider is set, it means the timeout was reached
                const provider = err.provider === undefined ? "-1" : err.provider;
                const locationString = `(${err.code}) ${err.error} [${provider}]`;
                resolve({ type: "gotLocation", data: { location: locationString } });
            }).finally(() => {
                if (then) {
                    inredningshjalpen.taskEnqueue(then());
                }
            })
        });
    },

    /**
     * @param {Lead} lead - The lead object
     * @param {File} file - The file object
     * @param {?number} timeout - The timeout in milliseconds
     * @returns {Task}
     */
    task_upload: (lead, file, timeout = 45_000) => {
        return new Promise((resolve) => {
            const timeoutPromise = helpers.timeoutPromise(timeout);
            const formData = new FormData();
            formData.set("file", file);
            formData.set("upload", "1");
            formData.set("token", lead.id);
            formData.set("id_shop_real", formData.get("id_shop"));
            formData.set("id_shop", 1);
            const uploadPromise = fetch("/lead", {
                method: "POST",
                headers: {
                    'Accept': 'application/json'
                },
                body: formData,
            });

            Promise.race([timeoutPromise, uploadPromise])
                .then(response => {
                    if (response.status === 400) {
                        response.json().then(data => {
                            resolve({ type: "fileUploadError", data: { error: { type: "bad_request", message: data.message } } });
                        }).catch(error => {
                            resolve({ type: "fileUploadError", data: { error: { type: "bad_request", message: "The file might be too large. Please try again with a smaller file." } } });
                        });
                    }
                    if (response.status === 200) {
                        return response.json();
                    }
                    throw new Error(`Server error: ${response.status}`);
                })
                .then(data => {
                    resolve({ type: "fileUploaded", data: { lead: data.lead } });
                })
                .catch(error => {
                    console.error("inredningshjalpen.js: task_upload: error", error);
                    resolve({ type: "fileUploadError", data: { error: { type: "other", message: error.message || String(error) } } });
                });
        });
    },

    task_finalizeLead: (lead, timeout = 15_000) => {
        const start = performance.now();
        const patchLead = inredningshjalpen.task_patch(lead, timeout);

        return new Promise((resolve) => {

            patchLead.then(msg => {
                if (msg.type === "patchError") {
                    resolve({ type: "finalizeLeadError", data: { error: { type: "patchError", message: msg.data.error.message } } });
                }
                if (msg.type === "patchSuccess") {
                    const timeoutPromise = helpers.timeoutPromise(timeout);
                    const formData = new FormData();
                    formData.set("token", msg.data.lead.id);
                    formData.set("seal", "1");
                    formData.set("revision", msg.data.lead.revision);
                    formData.set("id_shop_real", formData.get("id_shop"));
                    formData.set("id_shop", 1);
                    // Patch the lead to the server
                    const patchPromise = fetch("/lead", {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json'
                        },
                        body: formData,
                    })

                    Promise.race([timeoutPromise, patchPromise])
                        .then(response => {
                            if (!response.ok) {
                                return response.json().then(data => {
                                    throw new Error(`Server error: ${response.status}, ${JSON.stringify(data)}`);
                                });
                            }
                            return response.json();
                        })
                        .then(data => {
                            resolve({ type: "finalizeLeadSuccess", data: { lead: data.lead } });
                        })
                        .catch(error => {
                            console.error("inredningshjalpen.js: finalizeLead: error", error);
                            resolve({
                                type: "finalizeLeadError",
                                data: {
                                    error: {
                                        type: error === "timeout" ? "timeout" : "other",
                                        message: error.message || String(error)
                                    }
                                }
                            });
                        });
                }
            }).catch(error => {
                console.error("inredningshjalpen.js: task_finalizeLead: patchLead: error", error);
            });
        });
    },

    // ****************************************** //      
    // *                                        * //
    // *            HELPER FUNCTIONS            * //
    // *                                        * //
    // ****************************************** //

    /**
     * injectDataTags adds attributes stripped by the cms.
     * @param {string} langIso - The language ISO code
     * @param {HTMLFormElement} form - The form element
     * @throws {Error}
     * @returns {void}
    */
    injectDataTags: (langIso, form) => {
        formValidation.injectDataTag(true, formValidationRules.companyName, form.querySelector('#company_name'), langIso);
        formValidation.injectDataTag(true, formValidationRules.name, form.querySelector('#contact_name'), langIso);
        formValidation.injectDataTag(true, formValidationRules.email, form.querySelector('#contact_email'), langIso);
        formValidation.injectDataTag(true, formValidationRules.phone, form.querySelector('#contact_phone'), langIso);
    },

    /**
     * @param {string} langIso - The language ISO code
     * @param {HTMLFormElement} form - The form element
     * @throws {Error}
     * @returns {void}
     */
    injectDataTagsModal: (langIso, form) => {
        // TODO: inject data tags for the modal.. 
        // console.debug("inredningshjalpen.js: R380: injectDataTagsModal: langIso", langIso, "\nform", form);
        formValidation.injectDataTag(true, formValidationRules.orgNumber, form.querySelector("input#org_number"), langIso);
        formValidation.injectDataTag(true, formValidationRules.numberPositive, form.querySelector("input#number_of_seats"), langIso);
        formValidation.injectDataTag(false, formValidationRules.numberPositive, form.querySelector("input#budget"), langIso);
    },

    addTextAreaCounter: (form) => {
        const additionalNotes = form.querySelector("textarea#additional_notes");
        const additionalNotesInfo = additionalNotes?.nextElementSibling;
        if (!additionalNotes || !additionalNotesInfo) {
            console.error("inredningshjalpen.js: addTextAreaCounter: additionalNotes or additionalNotesInfo not found");
            return;
        }
        if (additionalNotesInfo.classList.contains("error-message")) {
            additionalNotesInfo.classList.add("info-message");
            additionalNotesInfo.classList.remove("error-message");
        }

        formValidation.textAreaCounter(additionalNotes, additionalNotesInfo, 850);
    },

    /**
     * @param {Lead} lead - The lead object
     * @throws {Error}
     * @returns {void}
     */
    modalPopulate: (lead) => {
        const form = document.querySelector("#inredningshjalpen-modal form");
        console.assert(form, "inredningshjalpen.js: R741: modalPopulate: form not found");

        form.querySelector("input#style_preference").value = lead.style_preference;
        form.querySelector("input#room_option").value = lead.room_option;
        form.querySelector("input#number_of_seats").value = lead.number_of_seats;
        form.querySelector("input#org_number").value = lead.org_number;
        form.querySelector("input#budget").value = lead.budget;
        form.querySelector("input#desired_delivery_date").value = lead.desired_delivery_date;
        form.querySelector("textarea#additional_notes").value = lead.additional_notes;
    },

    modalSetRoomOption: (roomOption = null) => {
        const modal = document.querySelector("#inredningshjalpen-modal");
        if (!modal) {
            return;
        }
        const roomOptionSelections = modal.querySelectorAll(".inredningshjalpen-modal-type-of-space-selection");
        roomOptionSelections.forEach((selection, i) => {
            selection.classList.remove("inredningshjalpen-modal-type-of-space-selection--active");
        });
        if (roomOption === "entire_office") {
            modal.querySelector("#inredningshjalpen-modal-type-of-space-selection-entire_office").classList.add("inredningshjalpen-modal-type-of-space-selection--active");
        }
        if (roomOption === "reception_area") {
            modal.querySelector("#inredningshjalpen-modal-type-of-space-selection-reception_area").classList.add("inredningshjalpen-modal-type-of-space-selection--active");
        }
        if (roomOption === "office_space") {
            modal.querySelector("#inredningshjalpen-modal-type-of-space-selection-office_space").classList.add("inredningshjalpen-modal-type-of-space-selection--active");
        }
        if (roomOption === "conference_room") {
            modal.querySelector("#inredningshjalpen-modal-type-of-space-selection-conference_room").classList.add("inredningshjalpen-modal-type-of-space-selection--active");
        }
        if (roomOption === "dining_area") {
            modal.querySelector("#inredningshjalpen-modal-type-of-space-selection-dining_area").classList.add("inredningshjalpen-modal-type-of-space-selection--active");
        }
        if (roomOption === "lounge_area") {
            const el = modal.querySelector("#inredningshjalpen-modal-type-of-space-selection-lounge_area")
            el.classList.add("inredningshjalpen-modal-type-of-space-selection--active");
        }
    },

    modalSetStylePreference: (stylePreference = null) => {
        const modal = document.querySelector("#inredningshjalpen-modal");
        if (!modal) {
            return;
        }
        const stylePreferenceSelections = modal.querySelectorAll(".inredningshjalpen-modal-type-of-style-selection");
        stylePreferenceSelections.forEach((selection, i) => {
            selection.classList.remove("inredningshjalpen-modal-type-of-style-selection--active");
        });
        if (stylePreference === "colorful") {
            modal.querySelector("#inredningshjalpen-modal-type-of-style-selection-colorful").classList.add("inredningshjalpen-modal-type-of-style-selection--active");
        }
        if (stylePreference === "elegant") {
            modal.querySelector("#inredningshjalpen-modal-type-of-style-selection-elegant").classList.add("inredningshjalpen-modal-type-of-style-selection--active");
        }
        if (stylePreference === "harmonious") {
            modal.querySelector("#inredningshjalpen-modal-type-of-style-selection-harmonious").classList.add("inredningshjalpen-modal-type-of-style-selection--active");
        }
        if (stylePreference === "industrial") {
            modal.querySelector("#inredningshjalpen-modal-type-of-style-selection-industrial").classList.add("inredningshjalpen-modal-type-of-style-selection--active");
        }
        if (stylePreference === "scandinavian") {
            modal.querySelector("#inredningshjalpen-modal-type-of-style-selection-scandinavian").classList.add("inredningshjalpen-modal-type-of-style-selection--active");
        }
        if (stylePreference === "surprise_me") {
            modal.querySelector("#inredningshjalpen-modal-type-of-style-selection-surprise_me").classList.add("inredningshjalpen-modal-type-of-style-selection--active");
        }
    },

    modalAddPlaceholders: (language) => {
        switch (language) {
            case "sv":
                $("input[name=budget]").attr("placeholder", "Ja, ange budget");
                $("input[name=number_of_seats]").attr("placeholder", "Skriv antal");
                $("#additional_notes").attr("placeholder", "Skriv här");
                break;
            case "de":
                $("input[name=budget]").attr("placeholder", "Ja, Budget angeben");
                $("input[name=number_of_seats]").attr("placeholder", "Anzahl ");
                $("#additional_notes").attr("placeholder", "Hier schreiben");
                break;
            case "fi":
                $("input[name=budget]").attr("placeholder", "Kyllä, anna budjetti");
                $("input[name=number_of_seats]").attr("placeholder", "Kirjoita määrä");
                $("#additional_notes").attr("placeholder", "Kirjoita tähän");
                break;
            case "dk":
                $("input[name=budget]").attr("placeholder", "Ja, indtast budget");
                $("input[name=number_of_seats]").attr("placeholder", "Skriv antal");
                $("#additional_notes").attr("placeholder", "Skriv her");
                break;
            case "fr":
                $("input[name=budget]").attr("placeholder", "Oui, préciser le budget");
                $("input[name=number_of_seats]").attr("placeholder", "Nombre");
                $("#additional_notes").attr("placeholder", "");
                break;
            case "nl":
                $("input[name=budget]").attr("placeholder", "Ja, geef budget aan");
                $("input[name=number_of_seats]").attr("placeholder", "Schrijf aantal");
                $("#additional_notes").attr("placeholder", "Schrijf hier");
                break;
            default:
                $("input[name=budget]").attr("placeholder", "Yes, specify budget");
                $("input[name=number_of_seats]").attr("placeholder", "Write number");
                $("#additional_notes").attr("placeholder", "");
                break;
        }
    },


    modalListeners: () => {
        const modalEl = document.querySelector("#inredningshjalpen-modal");
        // console.debug("inredningshjalpen.js: R565: modalListeners: \nmodal", modal);
        // mobile tooltip support
        if (helpers.isSmallDevice()) {
            $(".tooltip-trigger").click(function (event) {
                alert($(this).find(".tooltip-text").text());
            });
        }

        // next button
        const nextButtons = modalEl.querySelectorAll(".inredningshjalpen-modal__navigate--next");
        nextButtons.forEach((button, i) => {
            button.addEventListener("click", ev => {
                ev.preventDefault();
                inredningshjalpen.update({ type: "clickNext", data: null });
            });
        });

        // prev button
        const prevButtons = modalEl.querySelectorAll(".inredningshjalpen-modal__navigate--prev");
        prevButtons.forEach((button, i) => {
            button.addEventListener("click", ev => {
                ev.preventDefault();
                inredningshjalpen.update({ type: "clickPrev", data: null });
            });
        });

        // submit action
        formValidation.addListeners(modalEl.querySelector("form"),
            () => {
                inredningshjalpen.update({ type: "clickSendLead", data: null });
            }
        );

        // more blueprints
        const addMoreFile = modalEl.querySelector("#addMoreFile");
        if (addMoreFile) {
            addMoreFile.addEventListener("click", ev => {
                ev.preventDefault();
                modalEl.querySelectorAll("#file2, #file3, #file4, #file5").forEach(file => {
                    file.style.display = "block";
                });
                addMoreFile.style.display = "none";
            });
        }

        // more images
        const addMoreImages = modalEl.querySelector("#addMoreImages");
        if (addMoreImages) {
            addMoreImages.addEventListener("click", ev => {
                ev.preventDefault();
                modalEl.querySelectorAll("#file7, #file8, #file9, #file10").forEach(file => {
                    file.style.display = "block";
                });
                addMoreImages.style.display = "none";
            });
        }

        const rightImage = modalEl.querySelector(".inredningshjalpen-modal-right-image");
        // room_option
        // console.debug("inredningshjalpen.js: R469: room_option");
        try {
            const roomOptionInput = modalEl.querySelector("input#room_option");
            const roomOptionSelections = modalEl.querySelectorAll(".inredningshjalpen-modal-type-of-space-selection");
            const roomOption_entireOffice = document.querySelector("#inredningshjalpen-modal-type-of-space-selection-entire_office");
            roomOption_entireOffice.addEventListener("click", () => {
                rightImage.style.backgroundImage = "url('" + prestashop.urls.base_url + "themes/dpj/assets/img/digital-inredningshjalp/form-right-image.png')";
                roomOptionInput.value = "entire_office";
                roomOptionSelections.forEach((selection, i) => {
                    selection.classList.remove("inredningshjalpen-modal-type-of-space-selection--active");
                });
                roomOption_entireOffice.classList.add("inredningshjalpen-modal-type-of-space-selection--active");
                inredningshjalpen.update({ type: "userChangedField", data: { field: "room_option", value: "entire_office" } });
            });
            const roomOption_receptionArea = document.querySelector("#inredningshjalpen-modal-type-of-space-selection-reception_area");
            roomOption_receptionArea.addEventListener("click", () => {
                rightImage.style.backgroundImage = "url('" + prestashop.urls.base_url + "themes/dpj/assets/img/digital-inredningshjalp/form-right-image.png')";
                roomOptionInput.value = "reception_area";
                roomOptionSelections.forEach((selection, i) => {
                    selection.classList.remove("inredningshjalpen-modal-type-of-space-selection--active");
                });
                roomOption_receptionArea.classList.add("inredningshjalpen-modal-type-of-space-selection--active");
                inredningshjalpen.update({ type: "userChangedField", data: { field: "room_option", value: "reception_area" } });
            });
            const roomOption_officeSpace = document.querySelector("#inredningshjalpen-modal-type-of-space-selection-office_space");
            roomOption_officeSpace.addEventListener("click", () => {
                rightImage.style.backgroundImage = "url('" + prestashop.urls.base_url + "themes/dpj/assets/img/digital-inredningshjalp/form-right-image.png')";
                roomOptionInput.value = "office_space";
                roomOptionSelections.forEach((selection, i) => {
                    selection.classList.remove("inredningshjalpen-modal-type-of-space-selection--active");
                });
                roomOption_officeSpace.classList.add("inredningshjalpen-modal-type-of-space-selection--active");
                inredningshjalpen.update({ type: "userChangedField", data: { field: "room_option", value: "office_space" } });
            });
            const roomOption_conferenceRoom = document.querySelector("#inredningshjalpen-modal-type-of-space-selection-conference_room");
            roomOption_conferenceRoom.addEventListener("click", () => {
                rightImage.style.backgroundImage = "url('" + prestashop.urls.base_url + "themes/dpj/assets/img/digital-inredningshjalp/form-right-image.png')";
                roomOptionInput.value = "conference_room";
                roomOptionSelections.forEach((selection, i) => {
                    selection.classList.remove("inredningshjalpen-modal-type-of-space-selection--active");
                });
                roomOption_conferenceRoom.classList.add("inredningshjalpen-modal-type-of-space-selection--active");
                inredningshjalpen.update({ type: "userChangedField", data: { field: "room_option", value: "conference_room" } });
            });
            const roomOption_diningArea = document.querySelector("#inredningshjalpen-modal-type-of-space-selection-dining_area");
            roomOption_diningArea.addEventListener("click", () => {
                rightImage.style.backgroundImage = "url('" + prestashop.urls.base_url + "themes/dpj/assets/img/digital-inredningshjalp/form-right-image.png')";
                roomOptionInput.value = "dining_area";
                roomOptionSelections.forEach((selection, i) => {
                    selection.classList.remove("inredningshjalpen-modal-type-of-space-selection--active");
                });
                roomOption_diningArea.classList.add("inredningshjalpen-modal-type-of-space-selection--active");
                inredningshjalpen.update({ type: "userChangedField", data: { field: "room_option", value: "dining_area" } });
            });
            const roomOption_loungeArea = document.querySelector("#inredningshjalpen-modal-type-of-space-selection-lounge_area");
            roomOption_loungeArea.addEventListener("click", () => {
                rightImage.style.backgroundImage = "url('" + prestashop.urls.base_url + "themes/dpj/assets/img/digital-inredningshjalp/form-right-image.png')";
                roomOptionInput.value = "lounge_area";
                roomOptionSelections.forEach((selection, i) => {
                    selection.classList.remove("inredningshjalpen-modal-type-of-space-selection--active");
                });
                roomOption_loungeArea.classList.add("inredningshjalpen-modal-type-of-space-selection--active");
                inredningshjalpen.update({ type: "userChangedField", data: { field: "room_option", value: "lounge_area" } });
            });
        } catch (error) {
            console.error("inredningshjalpen.js: R551: room_option: error", error);
        }

        // style_preference
        // console.debug("inredningshjalpen.js: R367: style_preference");
        try {
            const stylePreferenceSelections = modalEl.querySelectorAll(".inredningshjalpen-modal-type-of-style-selection");
            const stylePreference_colorful = document.querySelector("#inredningshjalpen-modal-type-of-style-selection-colorful");
            const stylePreferenceInput = modalEl.querySelector("input#style_preference");
            stylePreference_colorful.addEventListener("click", () => {
                stylePreferenceInput.value = "colorful";
                stylePreferenceSelections.forEach((selection, i) => {
                    selection.classList.remove("inredningshjalpen-modal-type-of-style-selection--active");
                });
                stylePreference_colorful.classList.add("inredningshjalpen-modal-type-of-style-selection--active");
                rightImage.style.backgroundImage = "url('" + prestashop.urls.base_url + "themes/dpj/assets/img/digital-inredningshjalp/color-styles/colorful.png')";
                inredningshjalpen.update({ type: "userChangedField", data: { field: "style_preference", value: "colorful" } });
            });
            const stylePreference_elegant = document.querySelector("#inredningshjalpen-modal-type-of-style-selection-elegant");
            stylePreference_elegant.addEventListener("click", () => {
                stylePreferenceInput.value = "elegant";
                stylePreferenceSelections.forEach((selection, i) => {
                    selection.classList.remove("inredningshjalpen-modal-type-of-style-selection--active");
                });
                stylePreference_elegant.classList.add("inredningshjalpen-modal-type-of-style-selection--active");
                rightImage.style.backgroundImage = "url('" + prestashop.urls.base_url + "themes/dpj/assets/img/digital-inredningshjalp/color-styles/elegant.png')";
                inredningshjalpen.update({ type: "userChangedField", data: { field: "style_preference", value: "elegant" } });
            });
            const stylePreference_harmonious = document.querySelector("#inredningshjalpen-modal-type-of-style-selection-harmonious");
            stylePreference_harmonious.addEventListener("click", () => {
                stylePreferenceInput.value = "harmonious";
                stylePreferenceSelections.forEach((selection, i) => {
                    selection.classList.remove("inredningshjalpen-modal-type-of-style-selection--active");
                });
                stylePreference_harmonious.classList.add("inredningshjalpen-modal-type-of-style-selection--active");
                rightImage.style.backgroundImage = "url('" + prestashop.urls.base_url + "themes/dpj/assets/img/digital-inredningshjalp/color-styles/harmonious.png')";
                inredningshjalpen.update({ type: "userChangedField", data: { field: "style_preference", value: "harmonious" } });
            });
            const stylePreference_industrial = document.querySelector("#inredningshjalpen-modal-type-of-style-selection-industrial");
            stylePreference_industrial.addEventListener("click", () => {
                stylePreferenceInput.value = "industrial";
                stylePreferenceSelections.forEach((selection, i) => {
                    selection.classList.remove("inredningshjalpen-modal-type-of-style-selection--active");
                });
                stylePreference_industrial.classList.add("inredningshjalpen-modal-type-of-style-selection--active");
                rightImage.style.backgroundImage = "url('" + prestashop.urls.base_url + "themes/dpj/assets/img/digital-inredningshjalp/color-styles/industrial.png')";
                inredningshjalpen.update({ type: "userChangedField", data: { field: "style_preference", value: "industrial" } });
            });
            const stylePreference_scandinavian = document.querySelector("#inredningshjalpen-modal-type-of-style-selection-scandinavian");
            stylePreference_scandinavian.addEventListener("click", () => {
                stylePreferenceInput.value = "scandinavian";
                stylePreferenceSelections.forEach((selection, i) => {
                    selection.classList.remove("inredningshjalpen-modal-type-of-style-selection--active");
                });
                stylePreference_scandinavian.classList.add("inredningshjalpen-modal-type-of-style-selection--active");
                rightImage.style.backgroundImage = "url('" + prestashop.urls.base_url + "themes/dpj/assets/img/digital-inredningshjalp/color-styles/scandinavian.png')";
                inredningshjalpen.update({ type: "userChangedField", data: { field: "style_preference", value: "scandinavian" } });
            });
            const stylePreference_surpriseMe = document.querySelector("#inredningshjalpen-modal-type-of-style-selection-surprise_me");
            stylePreference_surpriseMe.addEventListener("click", () => {
                stylePreferenceInput.value = "surprise_me";
                stylePreferenceSelections.forEach((selection, i) => {
                    selection.classList.remove("inredningshjalpen-modal-type-of-style-selection--active");
                });
                stylePreference_surpriseMe.classList.add("inredningshjalpen-modal-type-of-style-selection--active");
                rightImage.style.backgroundImage = "url('" + prestashop.urls.base_url + "themes/dpj/assets/img/digital-inredningshjalp/color-styles/suprise_me.png')";
                inredningshjalpen.update({ type: "userChangedField", data: { field: "style_preference", value: "surprise_me" } });
            });
        } catch (error) {
            console.error("inredningshjalpen.js: 598: style_preference: error", error);
        }

        // add event listeners to all input and textarea fields
        modalEl.querySelectorAll("input").forEach(input => {
            input.addEventListener("change", (event) => {
                if (event.target.type === "file") {
                    // console.debug("inredningshjalpen.js: init: fileAdded: event", event);
                    inredningshjalpen.update({ type: "fileAdded", data: { file: event.target.files[0] } });
                    return;
                }
                inredningshjalpen.update({ type: "userChangedField", data: { field: event.target.name, value: event.target.value } });
            });
        });
        modalEl.querySelectorAll("textarea").forEach(textarea => {
            textarea.addEventListener("change", (event) => {
                inredningshjalpen.update({ type: "userChangedField", data: { field: event.target.name, value: event.target.value } });
            });
        });

        modalEl.querySelector(".inredningshjalpen-modal-location-reload").addEventListener("click", (event) => {
            event.preventDefault();
            // console.debug("inredningshjalpen.js: R1081: location reload");
            window.location.href = prestashop.urls.base_url;
        });

        /**
         * @todo remove this once we have a proper modal system
         * This is scissored from modals.js and modified. Might break if we ever change modals.js.
        */
        // modals.ol.on("click", ".close-modal", inredningshjalpen.update({ type: "modalClosed", data: null }));
        // modals.ol.click((e) => {
        //     if ($(e.target).hasClass("page-overlay") && !modals.ol.find(".not-closeable").length) { // checks if there are any modals that are prohibited from getting closed - like forced VAT popup on load
        //         inredningshjalpen.update({ type: "modalClosed", data: null });
        //     }
        // });

        /** @todo probably dont need this to be a task but I hesitate to have the update function be called as part from another context.. just a feeling though..*/
        modals.overlayCloseCallbacks.push(() => {
            inredningshjalpen.taskEnqueue(new Promise((resolve) => {
                resolve({ type: "modalClosed", data: null });
            }));
        });
    },

    /**
     * @param {boolean} loading
     */
    setLoading: (loading) => {
        const submitButton = document.querySelector("#submit_inredningshjalpen");
        submitButton.disabled = loading;
        submitButton.classList.toggle("submit_inredningshjalpen--is_loading", loading)
    },

    scrollToInitialForm: () => {
        helpers.scrollWindow("#contact-form-block", 150);
    },

    resetForm: () => {
        const form = document.querySelector("#inredningshjalpen-form");
        if (!form) {
            return;
        }
        form.querySelectorAll("input:not([type=hidden]):not([type=submit])").forEach(input => {
            input.value = ""
        })
        inredningshjalpen.modalSetStylePreference(null);
        inredningshjalpen.modalSetRoomOption(null);
    },
    formPopulate: (lead) => {
        const form = document.querySelector("#inredningshjalpen-form");
        form.querySelectorAll("input:not([type=hidden]):not([type=submit])").forEach(input => {
            input.value = lead[input.name] || "";
        })
    },

    getUrlParams: () => {
        const url = new URL(window.location.href);
        const id = url.searchParams.get('token');
        return { id };
    },

    /** 
     * @param {?string} id a
     */
    setUrlParams: (id = null) => {
        const url = new URL(window.location.href);
        if (id) {
            url.searchParams.set('token', id);
        } else {
            url.searchParams.delete('token');
        }
        window.history.pushState({}, '', url);
        // console.debug("inredningshjalpen.js: R445: setUrlParams: url", url);
    },

    /**
     * @param {HTMLFormElement} form
     * @param {number} step
     * @returns {{valid: boolean, errors: {field: string, message: string}[]}}
     */
    validateForm: (form, step) => {
        if (!step || step < 1 || step > 5) {
            throw new Error("inredningshjalpen.js: R775: validateForm: step is required and must be between 1 and 5");
        }
        if (!form) {
            throw new Error("inredningshjalpen.js: R776: validateForm: form is required");
        }

        // console.debug("inredningshjalpen.js: R777: validateForm: form", form);
        // console.debug("inredningshjalpen.js: R778: validateForm: step", step);
        const requiredFields = {
            1: ["company_name", "contact_name", "contact_email", "contact_phone"],
            2: ["room_option"],
            3: ["style_preference"],
            4: ["number_of_seats", "org_number"],
        }
        const formData = new FormData(form);
        const errors = [];

        if (step === 1) {
            // console.debug("inredningshjalpen.js: R782: validateForm: step 1: requiredFields", requiredFields[step]);
            requiredFields[step].forEach(field => {
                if (!formData.get(field) || formData.get(field) === "") {
                    errors.push({ field, message: "This field is required" });
                }
            });
            // console.debug("inredningshjalpen.js: R814: validateForm: step 1: errors", errors);
            // console.debug("inredningshjalpen.js: R815: validateForm: step 1: valid", errors.length === 0);
            return { valid: errors.length === 0, errors };
        }

        if (step >= 2) {
            // console.debug("inredningshjalpen.js: R823: validateForm: step 2: requiredFields", requiredFields[step]);
            requiredFields[step].forEach(field => {
                if (!formData.get(field) || formData.get(field) === "") {
                    errors.push({ field, message: "This field is required" });
                }
            });
        }
        if (step >= 3) {
            // console.debug("inredningshjalpen.js: R831: validateForm: step 3: requiredFields", requiredFields[step]);
            requiredFields[step].forEach(field => {
                if (!formData.get(field) || formData.get(field) === "") {
                    errors.push({ field, message: "This field is required" });
                }
            });
        }
        if (step >= 4) {
            // console.debug("inredningshjalpen.js: R839: validateForm: step 4: requiredFields", requiredFields[step]);
            requiredFields[step].forEach(field => {
                if (!formData.get(field) || formData.get(field) === "") {
                    errors.push({ field, message: "This field is required" });
                }
            });
        }

        // console.debug("inredningshjalpen.js: R814: validateForm: step 1: errors", errors);
        // console.debug("inredningshjalpen.js: R815: validateForm: step 1: valid", errors.length === 0);
        return { valid: errors.length === 0, errors };
    },
    showError: (error) => {
        /** @todo remove this and modal errors */
        alert(error);
    },

    // ****************************************** //      
    // *                                        * //
    // *               VALIDATION               * //
    // *                                        * //
    // ****************************************** //

    normalizeField: ({ field, value }) => {
        if (!value) {
            return { field, value: null };
        }

        // Remove whitespace number fields
        if (
            field === "budget" ||
            field === "number_of_seats"
        ) {
            value = value.replace(/\s/g, "");
            value = parseInt(value, 10);
            if (isNaN(value)) {
                value = null;
            }
        }

        // Limit additional_notes to 850 characters
        if (field === "additional_notes") {
            value = value.substring(0, 850);
        }

        return { field, value };
    },

    normalizeLead: (lead) => {
        const normalizedLead = {};
        for (const field in lead) {
            const { field: normalizedField, value: normalizedValue } = inredningshjalpen.normalizeField({ field, value: lead[field] });
            normalizedLead[normalizedField] = normalizedValue;
        }
        return normalizedLead;
    },

    /** @param {Msg} msg */
    validateMsg: (msg) => {
        // console.debug("inredningshjalpen.js: R1189: validateMsg: msg", msg);
        const requiredData = {
            "gotLeadFromServer": ["lead", "lead.id", "lead.company_name", "lead.contact_name", "lead.contact_email", "lead.contact_phone"],
            "gotLeadFromServerError": ["error", "error.message"],
            "userChangedField": ["field", "value"],
            "clickNext": null,
            "clickPrev": null,
            "clickSendLead": null,
            "modalClosed": null,
            "createLeadSuccess": ["lead"],
            "createLeadError": ["error", "error.type", "error.message"],
            "gotModalMarkup": ["modal"],
            "gotModalMarkupError": ["error", "error.type", "error.message"],
            "patchSuccess": ["lead", "lead.id"],
            "patchError": ["error", "error.type", "error.message"],
            "gotLocation": ["location"],
            "finalizeLeadSuccess": null,
            "finalizeLeadError": ["error", "error.type", "error.message"],
            "fileAdded": ["file"],
            "fileUploaded": ["lead"],
            "fileUploadError": ["error", "error.type", "error.message"],
            "noop": null,
        }
        if (!msg) {
            throw new Error("inredningshjalpen.js: R1190: validateMsg: msg is required");
        }
        if (!msg.type) {
            throw new Error("inredningshjalpen.js: R1191: validateMsg: msg.type is required");
        }
        // console.debug("inredningshjalpen.js: R1192: validateMsg: msg.type", msg.type);
        // console.debug("inredningshjalpen.js: R1193: validateMsg: requiredData[msg.type]", requiredData[msg.type]);
        if (typeof requiredData[msg.type] === "undefined") {
            throw new Error("inredningshjalpen.js: R1192: validateMsg: msg.type is not valid");
        }
        if (requiredData[msg.type]) {
            requiredData[msg.type].forEach(field => {
                if (field.includes('.')) {
                    const parts = field.split('.');
                    let value = msg.data;
                    for (const part of parts) {
                        if (typeof value?.[part] === "undefined") {
                            throw new Error(`inredningshjalpen.js: validateMsg: ${field} is required for ${msg.type}`);
                        }
                        value = value[part];
                    }
                } else {
                    if (typeof msg.data?.[field] === "undefined") {
                        throw new Error(`inredningshjalpen.js: validateMsg: ${field} is required for ${msg.type}`);
                    }
                }
            });
        }
    },
}